import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";

const inputClass = "mt-2 w-full rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", subject: "", message: "", website: "" });
  const [state, setState] = useState({ sending: false, success: "", error: "" });

  useEffect(() => { document.title = "Contact Us | QR Code Creator Studio"; }, []);

  const update = (key) => (event) => setForm((previous) => ({ ...previous, [key]: event.target.value }));

  async function submit(event) {
    event.preventDefault();
    setState({ sending: true, success: "", error: "" });

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.detail || "Unable to send your message.");

      setForm({ name: "", email: "", company: "", subject: "", message: "", website: "" });
      setState({ sending: false, success: "Thank you. Your message has been sent and we will get back to you soon.", error: "" });
    } catch (error) {
      setState({ sending: false, success: "", error: error.message || "Unable to send your message." });
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
        <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
          <section>
            <p className="text-sm font-semibold text-primary">CONTACT US</p>
            <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight">How can we help?</h1>
            <p className="mt-5 max-w-md text-base leading-7 text-muted-foreground">
              Have a question about QR Code Creator Studio, your account, or creating QR codes? Send us a message and our team will reply by email.
            </p>
            <div className="mt-8 rounded-2xl border border-border bg-card p-5">
              <p className="font-display font-bold">Email support</p>
              <a className="mt-2 inline-block text-primary font-semibold hover:underline" href="mailto:qrcodecreatorstudio@gmail.com">
                qrcodecreatorstudio@gmail.com
              </a>
            </div>
          </section>

          <section className="rounded-2xl border border-border bg-card p-5 sm:p-8">
            <h2 className="font-display text-2xl font-bold">Send a message</h2>
            <p className="mt-2 text-sm text-muted-foreground">Fields marked with * are required.</p>

            {state.success && <div className="mt-5 rounded-xl bg-green-500/10 px-4 py-3 text-sm text-green-700 dark:text-green-400">{state.success}</div>}
            {state.error && <div className="mt-5 rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive">{state.error}</div>}

            <form className="mt-6 space-y-5" onSubmit={submit}>
              <div className="hidden" aria-hidden="true">
                <label>Website <input tabIndex="-1" autoComplete="off" value={form.website} onChange={update("website")} /></label>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <label className="text-sm font-semibold">Full name *
                  <input className={inputClass} value={form.name} onChange={update("name")} minLength="2" maxLength="120" required />
                </label>
                <label className="text-sm font-semibold">Email address *
                  <input className={inputClass} type="email" value={form.email} onChange={update("email")} maxLength="254" required />
                </label>
              </div>

              <label className="block text-sm font-semibold">Company
                <input className={inputClass} value={form.company} onChange={update("company")} maxLength="160" />
              </label>

              <label className="block text-sm font-semibold">Subject *
                <input className={inputClass} value={form.subject} onChange={update("subject")} minLength="3" maxLength="160" required />
              </label>

              <label className="block text-sm font-semibold">Message *
                <textarea className={`${inputClass} min-h-36 resize-y`} value={form.message} onChange={update("message")} minLength="10" maxLength="5000" required />
              </label>

              <button type="submit" disabled={state.sending} className="w-full rounded-xl bg-primary px-5 py-3 font-semibold text-primary-foreground transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60">
                {state.sending ? "Sending..." : "Send message"}
              </button>
            </form>
          </section>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
